import React, { useState } from 'react';
import type { FC } from 'react';
import { isType, match, partial } from 'variant';
import { useBiLogger } from '@wix/cli-telemetry-react';
import { useAsync, useAsyncCallback } from '@wix/cli-ui-kit';
import { useDevCenterClient } from '@wix/dev-center-client';
import { RepoType } from '@wix/package-manager';
import type { GenerationData } from '../../types.js';
import { ChooseAppName } from '../ChooseAppName.js';
import { ChoosePackageName } from '../ChoosePackageName.js';
import type {
  AppNameOptions,
  TemplateSource,
} from '../../parse-command-options.js';
import { ChooseTemplateFlow } from './ChooseTemplateFlow.js';
import { RegisteringApp } from '../RegisteringApp.js';
import { AppRegistered } from '../AppRegistered.js';
import { ConfigMcp } from '../mcp/ConfigMcp.js';
import { getMcpResult, McpConfigResult } from '../mcp/getMcpResult.js';

interface NewAppFlowProps {
  template?: TemplateSource;
  appName?: AppNameOptions;
  targetParentFolder: string;
  repoType: RepoType;
  onSubmit: (data: GenerationData) => void;
}

interface CreateAppCallbackArgs {
  appName: string;
  packageName: string;
  template: TemplateSource;
  mcpConfigResult: McpConfigResult;
}

export const NewAppFlow: FC<NewAppFlowProps> = ({
  onSubmit,
  template: templateFromOptions,
  appName: appNameFromOptions,
  targetParentFolder,
  repoType,
}) => {
  const devCenterClient = useDevCenterClient();
  const biLogger = useBiLogger();

  const [templateData, setTemplateData] = useState<TemplateSource | undefined>(
    templateFromOptions
  );
  const [appName, setAppName] = useState<string>();
  const [packageName, setPackageName] = useState<string>();

  const createAppCallback = useAsyncCallback(
    async (
      _,
      { appName, packageName, template, mcpConfigResult }: CreateAppCallbackArgs
    ) => {
      const { id: appId } = await match(
        template,
        partial({
          App: ({ payload }) =>
            devCenterClient.createAppFromTemplate(
              {
                name: appName,
                templateId: payload.id,
              },
              payload
            ),
          default: () => devCenterClient.createApp({ name: appName }),
        })
      );

      biLogger.updateDefaults({
        _appId: appId,
      });

      onSubmit({
        template,
        appData: { appName, appId },
        packageName,
        mcpConfigResult,
      });

      return {
        appId,
        appName,
      };
    }
  );

  const autoCreateApp =
    isType(appNameFromOptions, 'Provided') && !!templateFromOptions;

  useAsync(async () => {
    if (autoCreateApp) {
      return createAppCallback.execute({
        appName: appNameFromOptions.appName,
        packageName: appNameFromOptions.packageName,
        template: templateFromOptions,
        mcpConfigResult: McpConfigResult.Canceled(),
      });
    }
  }, []);

  return (
    <>
      {!templateFromOptions && (
        <ChooseTemplateFlow onTemplateChoose={setTemplateData} />
      )}

      {templateData && !autoCreateApp && (
        <ChooseAppName onSubmit={setAppName} />
      )}

      {appName && templateData && !autoCreateApp && (
        <ChoosePackageName
          appName={appName}
          targetParentFolder={targetParentFolder}
          repoType={repoType}
          onSelected={(userPackageName) => {
            // If it's monorepo, then I will not ask for configuring MCP.
            // Monorepo is the niche case, that we want to support only for internal devs.
            if (isType(repoType, RepoType.Monorepo)) {
              void createAppCallback.execute({
                appName,
                packageName: userPackageName,
                template: templateData,
                mcpConfigResult: McpConfigResult.Canceled(),
              });
            } else {
              setPackageName(userPackageName);
            }
          }}
        />
      )}

      {appName && packageName && templateData && !autoCreateApp && (
        <>
          <ConfigMcp
            onSelected={(ideForMcp) => {
              void createAppCallback.execute({
                appName,
                packageName,
                template: templateData,
                mcpConfigResult: getMcpResult({
                  ideForMcp,
                  targetParentFolder,
                  packageName,
                }),
              });
            }}
          />
        </>
      )}

      {match(createAppCallback.status, {
        Error: () => null,
        Loading: () => <RegisteringApp />,
        Success: ({ result }) => <AppRegistered {...result} />,
        NotRequested: () => null,
      })}
    </>
  );
};
